import React, { useState } from 'react';
import { ExternalLink, X } from 'lucide-react';

/**
 * Preview → Production redirect banner.
 *
 * The Emergent preview URL (property-3d-showcase-2.preview.emergentagent.com)
 * is shared via WhatsApp/email campaigns and old bookmarks. Visitors who
 * land on it are real prospects who should be on the production domain
 * (shopperden.com) where Stripe/Meta production keys are live. This banner
 * makes the switch one click — preserving any sign-up intent.
 *
 * Behaviour:
 *  - Renders ONLY on preview/dev hosts (never on shopperden.com)
 *  - Sticky top, gold-on-navy, one-tap "Go to shopperden.com"
 *  - User-dismissible (per-session via sessionStorage), but reappears next visit
 *  - Preserves the current path: /buyer → shopperden.com/buyer
 */
const PROD_HOST = 'shopperden.com';

const isPreviewHost = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host.endsWith('.preview.emergentagent.com') || host.endsWith('.emergent.host');
};

export const PreviewToProdBanner = () => {
  // Compute initial visibility at mount-time (lazy initializer). The lint
  // rule `react-hooks/set-state-in-effect` rightly bars us from doing this
  // inside useEffect; doing it as the initial state is the correct shape.
  const [show, setShow] = useState(() => {
    if (!isPreviewHost()) return false;
    try {
      if (sessionStorage.getItem('previewBannerDismissed') === '1') return false;
    } catch (_e) { /* ignore */ }
    return true;
  });

  const dismiss = () => {
    try { sessionStorage.setItem('previewBannerDismissed', '1'); } catch (_e) { /* ignore */ }
    setShow(false);
  };

  const goToProd = () => {
    const path = (window.location.pathname || '/') + (window.location.search || '');
    window.location.href = `https://${PROD_HOST}${path}`;
  };

  if (!show) return null;

  return (
    <div
      data-testid="preview-to-prod-banner"
      className="fixed top-0 left-0 right-0 z-[60] bg-[#0F172A] border-b-2 border-[#D4AF37] text-white"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <span
          className="hidden sm:inline-block text-[10px] tracking-[0.18em] uppercase font-bold text-[#D4AF37]"
        >
          Preview · dev
        </span>
        <p className="flex-1 text-sm">
          You&apos;re on our preview environment. The live site with real listings
          is at <b>shopperden.com</b> — switch to keep your sign-up.
        </p>
        <button
          onClick={goToProd}
          data-testid="preview-to-prod-cta"
          className="inline-flex items-center gap-1.5 bg-[#D4AF37] hover:bg-[#C19B2E] text-[#0F172A] text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded transition-colors"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Go to shopperden.com
        </button>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="preview-to-prod-dismiss"
          className="text-white/40 hover:text-white/80 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default PreviewToProdBanner;
